"use client";

import {ThemeProvider} from "@mui/material/styles";
import {darkTheme, lightTheme} from "./themes";
import React, {createContext, useContext, useEffect, useState} from "react";
import {Theme} from "@mui/material";
import Cookies from "js-cookie";
import {updateAppTheme} from "@/redux/slices/settings";
import {AsyncCookie} from "@/components/shared/client/utils";
import {useDispatch} from "react-redux";

interface ThemeContextType {
    updateTheme: (key: number) => void;
    themes: Theme[];
    theme: number
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useCustomTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error("useTheme must be used within a ThemeProvider");
    }
    return context;
};

const ThemeContextProvider = ({children}: { children: React.ReactNode }) => {
    const dispatch = useDispatch();
    const [theme, setTheme] = useState<number>(1);
    const [mounted, setMounted] = useState(false); // Ensure hydration consistency
    const themes = [lightTheme, lightTheme, darkTheme]
    const cookieName = process.env.NEXT_PUBLIC_COOKIE_PREFIX + "_" + "CUSTOM_DATA";
    let customData = JSON.parse(Cookies.get(cookieName) ?? JSON.stringify({theme: 1, dir: 'ltr', locale: "en"}));

    useEffect(() => {
        setThemeData();
        setMounted(true);
    }, []);

    const setThemeData = () => {
        updateTheme(customData.theme);
    }

    const updateTheme = (newTheme: number) => {
        setTheme(newTheme);
        dispatch(updateAppTheme(newTheme));
        customData = {
            ...customData,
            theme: newTheme
        }
        AsyncCookie.setCookie(cookieName, JSON.stringify(customData), 365)
    };

    if (!mounted) return null;

    return <ThemeContext.Provider value={{themes, theme, updateTheme}}>
        <ThemeProvider theme={themes[theme]}>
            {children}
        </ThemeProvider>
    </ThemeContext.Provider>
}

export default ThemeContextProvider;